import { Component, Element, Prop, h, Watch, Listen, Method, Event, EventEmitter, Host } from '@stencil/core';
import { focusElement, isDescendant, getFocusableElementList } from '../../utils/functions';
import { KEY_CODES } from '../../utils/variables';

const VERTICAL_OFFSET = 10;

@Component({
  tag: 'som-popover',
  styleUrl: 'som-popover.scss',
})
export class SomPopover {
  resizeTimeout;

  forEl: HTMLElement;

  popupContainerEl: HTMLDivElement;

  focusedElement: HTMLElement = null;

  @Element() el: HTMLElement;

  /**
   * This property is used to associate the popover with the corresponding element. The corresponding elements `ID` should be used as the value. 
   */
  @Prop() for: string;

  /**
   * This property controls the horizontal position of the popover.
   */
  @Prop() horizontalPosition: 'left' | 'right' = 'left';

  /**
   * 	This property controls the vertical position of the popover.
   */
  @Prop() verticalPosition: 'top' | 'bottom' = 'bottom';

  // This Prop isn't being used 
  //@Prop() relativePositioning = false;

  /**
   * This boolean property is used to display or hide the popover.
   */
  @Prop({ mutable: true, reflect: true }) show = false;

  focusedElBeforeOpen;

  @Watch('show')
  handleShowChange() {
    this.updatePosition();
    if (this.show) {
      this.focusedElBeforeOpen = document.activeElement;
      this.popoverOpened.emit();
    } else {
      this.popoverClosed.emit();
    }
  }

  /**
   * This event is triggered after popover is opened.
   */
  @Event() popoverOpened: EventEmitter;

  /**
   * This event is triggered after popover is closed.
   */
  @Event() popoverClosed: EventEmitter;


  @Listen('keydown')
  handleKeydown(e) {
    if (this.show && e.keyCode === KEY_CODES.ESC) {
      e.preventDefault();
      e.stopPropagation();
      this.show = false;
      this.popoverClosed.emit();
      focusElement(this.focusedElBeforeOpen);
    }
  }

  @Listen('click', { target: 'window' })
  handleWindowClick(e) {
    if (this.forEl !== e.target && !isDescendant(this.forEl, e.target)) {
      // Click did not happen on the "for" element
      if (this.popupContainerEl && !isDescendant(this.popupContainerEl, e.target) && !this.popupContainerEl.classList.contains('som-popover-hidden')) {
        // Popop is not already hidden in the DOM
        this.show = false;
      }
    }
  }

  @Listen('resize', { target: 'window' })
  handleWindowResize() {
    clearTimeout(this.resizeTimeout);

    // Setup debounce so we're not constantly reading screen width
    this.resizeTimeout = setTimeout(this.updatePosition.bind(this), 300);
  }

  @Listen('keydown')
  handleKeypress(e) {
    if (this.show) {
      if (e.keyCode === KEY_CODES.TAB) {
        e.preventDefault();
        e.stopPropagation();
        const focusableElements = this.getFocusableElements();
        const focusedElementIndex = focusableElements.findIndex(element => element === document.activeElement);

        if (focusableElements.length > 0) {
          // Popover is shown, let's capture the tab
          let newIndex = 0;
          if (e.shiftKey) {
            if (focusedElementIndex === 0) {
              newIndex = focusableElements.length - 1;
            } else {
              newIndex = focusedElementIndex - 1;
            }
          } else {
            newIndex = (focusedElementIndex + 1) % focusableElements.length;
          }
          this.focusedElement = focusableElements[newIndex];
          focusElement(this.focusedElement);
        }
      } 
    } 
  }

  /**
   * This method will open the popover.
   * @returns Promise will resolve when the open animation ends.
   */
  @Method()
  open(): Promise<void> {
    return new Promise(resolve => {
      if (!this.show) {
        this.show = true;

        // Triggers that the popover is open
        this.popoverOpened.emit();

        resolve();
      } else {
        // popover is already opened
        resolve();
      }
    });
  }

  /**
   * This method will close the popover.
   * @returns Promise will resolve when the close animation ends.
   */
  @Method()
  close(): Promise<void> {
    return new Promise(resolve => {
      if (this.show) {
        this.show = false;

        // Triggers that the popover is closed
        this.popoverClosed.emit();

        resolve();
      } else {
        // popover is already closed
        resolve();
      }
    });
  }

  /**
   * This method is emitted when the popover gets focus.
   */
  @Method()
  async hasFocus() {
    return this.getFocusableElements().includes(document.activeElement);
  }

  getFocusableElements() {
    return [].slice.call(this.popupContainerEl.querySelectorAll(getFocusableElementList()));
  }

  updatePosition() {
    // Reset positioning
    this.popupContainerEl.style.top = '';
    this.popupContainerEl.style.bottom = '';
    this.popupContainerEl.style.left = '';
    this.popupContainerEl.style.right = '';
    this.popupContainerEl.classList.add('som-popover-hidden');

    if (this.show) {
      this.popupContainerEl.classList.remove('som-popover-hidden');

      if (this.verticalPosition === 'top') {
        this.popupContainerEl.style.bottom = `${this.getBottomPosition()}px`;
      } else {
        this.popupContainerEl.style.top = `${this.getTopPosition()}px`;
      }

      if (this.horizontalPosition === 'right') {
        this.popupContainerEl.style.right = `${this.getRightPosition()}px`;
      } else {
        this.popupContainerEl.style.left = `${this.getLeftPosition()}px`;
      }
    }
  }

  getBottomPosition() {
    return this.forEl.offsetParent.clientHeight - this.forEl.offsetTop + VERTICAL_OFFSET;
  }

  getTopPosition() {
    return this.forEl.offsetParent.clientTop + this.forEl.offsetTop + this.forEl.offsetHeight + VERTICAL_OFFSET;
  }

  getLeftPosition() {
    return this.forEl.offsetParent.clientLeft + this.forEl.offsetLeft;
  }

  getRightPosition() {
    return this.forEl.offsetParent['offsetWidth'] - this.forEl.offsetLeft - this.forEl.offsetWidth;
    //return this.forEl.offsetParent.clientWidth - this.forEl.offsetLeft - this.forEl.offsetWidth;
  }

  componentDidLoad() {
    this.popupContainerEl = this.el.querySelector('.som-popover-container');
    this.forEl = document.getElementById(this.for);
  }

  render() {
    return (
      <Host aria-modal="true" role="dialog" tabindex="-1" aria-labelledby={this.for}>
        <div class="som-popover-container som-popover-hidden">
          <div class="som-popover-content">
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
